import React, { PropsWithChildren } from 'react';
import Skeleton from '@bharatpe/bharat-ui/web/SkeletonUI';

interface IProps {
	wrapperClassName?: string;
	variant?: 'rect' | 'circle' | 'text';
	height?: number | string;
	width?: number | string;
	animation?: 'pulse' | 'wave' | false;
	component?: string;
}

const WrapperComp: React.FC<PropsWithChildren<{ condition: boolean; wrapperClassName?: string }>> = ({
	condition = false,
	children,
	wrapperClassName,
}) => (condition ? <div className={wrapperClassName}>{children}</div> : <>{children}</>);

export const CardShimmer: React.FC<IProps> = ({ wrapperClassName = '', ...remainingProps }) => {
	const propsObject = {
		variant: 'rect',
		...remainingProps,
	};

	return (
		<WrapperComp condition={wrapperClassName.trim().length > 0} wrapperClassName={wrapperClassName}>
			<Skeleton variant="skeleton" className="br2" options={propsObject} />
		</WrapperComp>
	);
};

export const TextShimmer: React.FC<IProps & { className?: string }> = ({ className = '', ...remainingProps }) => {
	const propsObject = {
		variant: 'text',
		...remainingProps,
	};
	return <Skeleton variant="skeleton" options={propsObject} className={className} />;
};

export const ListShimmer: React.FC = () => {
	return (
		<Skeleton
			variant="list"
			className="mx4 mt5 width-auto"
			options={{
				list: [
					{
						title: 'abc',
						subtitle: 'abcd',
					},

					{
						title: 'abc',
					},
				],
			}}
		/>
	);
};

export const SectionHeaderShimmer: React.FC<{ wrapperClassName?: string; showRightContent?: boolean }> = ({
	wrapperClassName = '',
	showRightContent = true,
}) => {
	return (
		<div className={wrapperClassName}>
			<TextShimmer width={'35%'} height={18} component="div" />
			{showRightContent && <TextShimmer width={'20%'} height={18} component="div" />}
		</div>
	);
};

export const SectionHeaderWithCardShimmer: React.FC<{
	sectionHeaderWrapperClass: string;
	cardWrapperClass?: string;
	showRightContent?: boolean;
	cardHeight?: number | string;
	cardWidth?: number | string;
}> = ({
	sectionHeaderWrapperClass,
	cardWrapperClass,
	showRightContent = true,
	cardHeight = 120,
	cardWidth = '100%',
}) => {
	return (
		<>
			<SectionHeaderShimmer wrapperClassName={sectionHeaderWrapperClass} showRightContent={showRightContent} />
			<CardShimmer wrapperClassName={cardWrapperClass} height={cardHeight} width={cardWidth} />
		</>
	);
};

export const DummyPageShimmer: React.FC = () => {
	return (
		<>
			<CardShimmer wrapperClassName="mx4 mt6 mb6" height={160} width="100%" />
			<SectionHeaderWithCardShimmer
				sectionHeaderWrapperClass="mx4 mb5"
				cardWrapperClass="mx4 mb6"
				showRightContent={false}
				cardHeight={200}
			/>
			<SectionHeaderShimmer wrapperClassName="mx4 flex flex-justify" />
			<ListShimmer />
			<CardShimmer wrapperClassName="mx4 mt5" height={160} width="100%" />
		</>
	);
};

export const InvoicesShimmer: React.FC = () => {
	const dummyListItemsForShimmer = new Array(5).fill(1).map((e, idx) => ({ title: (idx + 1).toString() }));
	return (
		<Skeleton
			variant="list"
			className="mx4 mt16 width-auto"
			options={{
				list: dummyListItemsForShimmer,
			}}
		/>
	);
};

export const InstructionVideoShimmer: React.FC = () => {
	return (
		<SectionHeaderWithCardShimmer
			sectionHeaderWrapperClass="mx4 mt4 mb2"
			cardWrapperClass="mx4 mb4"
			showRightContent={false}
			cardHeight={196}
		/>
	);
};

export const QuickLinkShimmer: React.FC = () => {
	return (
		<SectionHeaderWithCardShimmer
			sectionHeaderWrapperClass="mx4 my6"
			cardWrapperClass="mx4 mb4"
			showRightContent={false}
		/>
	);
};

export const FaqListShimmer: React.FC = () => {
	return (
		<>
			<SectionHeaderWithCardShimmer
				sectionHeaderWrapperClass="mx4 mt4 mb2 flex flex-center flex-justify"
				cardWrapperClass="mx4 mb4"
				showRightContent={true}
				cardHeight={120}
			/>
		</>
	);
};

export const TestimonialShimmer: React.FC = () => {
	return (
		<>
			<SectionHeaderWithCardShimmer
				sectionHeaderWrapperClass="mx4 my6"
				cardWrapperClass="mx4 mb4"
				showRightContent={false}
				cardHeight={160}
			/>
		</>
	);
};

export const RecommendationShimmer: React.FC = () => {
	return (
		<>
			<SectionHeaderWithCardShimmer
				sectionHeaderWrapperClass="mx4 my6"
				cardWrapperClass="mx4 mb4"
				showRightContent={false}
				cardHeight={160}
			/>
		</>
	);
};

export const TransactionListCardShimmer: React.FC = () => {
	return (
		<>
			<SectionHeaderShimmer wrapperClassName="mx4 flex flex-justify my6" />
			<ListShimmer />
		</>
	);
};

export const ChatSupportShimmer: React.FC = () => {
	return <CardShimmer wrapperClassName="my3 mx4" height={48} width="100%" />;
};
